/**
 * HTML Sanitization Utility
 * Prevents XSS attacks by sanitizing HTML content before rendering
 *
 * @module utils/sanitize
 */
import DOMPurify from "isomorphic-dompurify";

/**
 * Configuration for DOMPurify sanitization
 */
const SANITIZE_CONFIG = {
  // Allow basic formatting tags
  ALLOWED_TAGS: [
    "p",
    "br",
    "b",
    "i",
    "em",
    "strong",
    "a",
    "ul",
    "ol",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "blockquote",
    "code",
    "pre",
    "span",
    "div",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "img",
    "hr",
  ],
  // Allow safe attributes
  ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "id", "style", "target", "rel", "width", "height"],
  // Only allow safe URLs for href and src
  ALLOWED_URI_REGEXP:
    /^(?:(?:(?:f|ht)tps?|mailto|tel|data:image\/(?:gif|png|jpeg|webp)):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  // Forbid dangerous protocols
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  FORBID_TAGS: ["script", "style", "iframe", "form", "input", "object", "embed"],
  // Keep safe content
  KEEP_CONTENT: true,
  // Add rel="noopener" to links
  ADD_ATTR: ["rel"],
};

/**
 * Strict configuration for medical/clinical content
 * Only allows basic text formatting
 */
const STRICT_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "b", "i", "em", "strong", "span", "div", "ul", "ol", "li"],
  ALLOWED_ATTR: ["class"],
  KEEP_CONTENT: true,
};

/**
 * Configuration for highlighted search results (Algolia)
 * Only allows mark and em tags for highlighting
 */
const HIGHLIGHT_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["mark", "em", "b", "strong", "span"],
  ALLOWED_ATTR: ["class"],
  KEEP_CONTENT: true,
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(dirty: string | undefined | null): string {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, SANITIZE_CONFIG);
}

/**
 * Sanitize HTML with strict settings for clinical content
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string with minimal tags
 */
export function sanitizeHtmlStrict(dirty: string | undefined | null): string {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, STRICT_SANITIZE_CONFIG);
}

/**
 * Sanitize highlighted search results (from Algolia)
 * @param dirty - The potentially unsafe HTML string with highlights
 * @returns Sanitized HTML string preserving highlight marks
 */
export function sanitizeHighlight(dirty: string | undefined | null): string {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, HIGHLIGHT_SANITIZE_CONFIG);
}

/**
 * Convert newlines to <br> tags safely
 * @param text - Text with newlines
 * @returns HTML string with <br> tags
 */
export function textToHtml(text: string | undefined | null): string {
  if (!text) return "";
  // First escape any HTML entities, then convert newlines
  const escaped = DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return escaped.replace(/\n/g, "<br>");
}

/**
 * Sanitize and convert newlines to <br> for display
 * Use this for AI-generated content that may contain newlines
 * @param content - Content with potential newlines
 * @returns Safe HTML with <br> tags
 */
export function sanitizeWithLineBreaks(content: string | undefined | null): string {
  if (!content) return "";
  // Sanitize first, then replace newlines with br
  const sanitized = sanitizeHtmlStrict(content);
  return sanitized.replace(/\n/g, "<br>");
}

/**
 * Create safe HTML props for dangerouslySetInnerHTML
 * @param content - Content to sanitize
 * @param mode - Sanitization mode
 * @returns Object safe for spreading into dangerouslySetInnerHTML
 */
export function createSafeHtml(
  content: string | undefined | null,
  mode: "default" | "strict" | "highlight" | "linebreaks" = "default"
): { __html: string } {
  let sanitized: string;

  switch (mode) {
    case "strict":
      sanitized = sanitizeHtmlStrict(content);
      break;
    case "highlight":
      sanitized = sanitizeHighlight(content);
      break;
    case "linebreaks":
      sanitized = sanitizeWithLineBreaks(content);
      break;
    default:
      sanitized = sanitizeHtml(content);
  }

  return { __html: sanitized };
}

export default DOMPurify;
